import { useState } from 'react';
import { Button, Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { AdminGatewayAccount, AdminGatewayAccountListFilters } from '@/api/models';
import { FiltersPopover } from '@/components/shell/FiltersPopover';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { useCanManageAccess } from '@/access/useCanManageAccess';
import { useTestBenchAvailability } from '@/features/test-bench/useTestBenchAvailability';
import { countActiveGatewayAccountFilters } from './filters/activeFilterCount';
import { GatewayAccountsFiltersForm } from './filters/GatewayAccountsFiltersForm';
import { GatewayAccountsListView } from './GatewayAccountsListView';
import { GatewayAccountCreateDialog } from './GatewayAccountCreateDialog';
import { TestBenchPresetLinkDialog } from './TestBenchPresetLinkDialog';

const DEFAULT_FILTERS: AdminGatewayAccountListFilters = { page: 1, pageSize: 50 };
const PAGINATION_ONLY_KEYS = new Set(['page', 'pageSize']);

/**
 * Gateway accounts surface against `/v1/admin/gateway-accounts` (A3's
 * contract). `AdminGatewayAccount` never carries any credential value —
 * that only ever flows through `.../secrets`, wired on the detail screen.
 * `platform:auditor` sees the same list read-only.
 */
export function GatewayAccountsPage() {
  const translate = useTranslate();
  const navigate = useNavigate();
  const canWrite = useCanManageAccess();
  const { available: testBenchAvailable } = useTestBenchAvailability();
  const [filters, setFilters] = useState<AdminGatewayAccountListFilters>(DEFAULT_FILTERS);
  const [refreshToken, setRefreshToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [presetLinkOpen, setPresetLinkOpen] = useState(false);

  function updateFilters(patch: Partial<AdminGatewayAccountListFilters>) {
    const isPaginationOnly = Object.keys(patch).every((key) => PAGINATION_ONLY_KEYS.has(key));
    setFilters((prev) => ({ ...prev, ...patch, page: isPaginationOnly ? (patch.page ?? prev.page) : 1 }));
  }

  function handleCreated(account: Pick<AdminGatewayAccount, 'id'>) {
    setCreateOpen(false);
    setRefreshToken((token) => token + 1);
    navigate(`/gateway-accounts/${encodeURIComponent(account.id)}`);
  }

  function handlePresetLinked(account: Pick<AdminGatewayAccount, 'id'>) {
    setPresetLinkOpen(false);
    setRefreshToken((token) => token + 1);
    navigate(`/gateway-accounts/${encodeURIComponent(account.id)}`);
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        left={
          <PageTitle
            title={translate('pay.gatewayAccounts.pageTitle')}
            subtitle={translate('pay.gatewayAccounts.pageSubtitle')}
          />
        }
        filters={
          <FiltersPopover
            activeCount={countActiveGatewayAccountFilters(filters)}
            onClear={() => setFilters(DEFAULT_FILTERS)}
          >
            <GatewayAccountsFiltersForm filters={filters} onChange={updateFilters} />
          </FiltersPopover>
        }
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onCreate={canWrite ? () => setCreateOpen(true) : undefined}
        createLabel={translate('pay.gatewayAccounts.list.createLabel')}
        after={
          canWrite && testBenchAvailable ? (
            <Button size="small" variant="outlined" onClick={() => setPresetLinkOpen(true)}>
              {translate('pay.testBench.preset.linkAction')}
            </Button>
          ) : undefined
        }
      />
      <GatewayAccountsListView key={refreshToken} filters={filters} onFiltersChange={updateFilters} />
      <GatewayAccountCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <TestBenchPresetLinkDialog open={presetLinkOpen} onClose={() => setPresetLinkOpen(false)} onLinked={handlePresetLinked} />
    </Stack>
  );
}
